import { ModelEntityType, productLikeEntityTypes } from '@upright/common/model/input/types';
import { safeVersionGte } from '@upright/common/utils';
import { useGetAvailableReleases } from '@upright/ui/api/model/releases';
import { getVersionAndRelease, getVersionAndReleaseIfExists } from '@upright/ui/untidy/redux/selectors';
import { useSelector } from 'react-redux';

export function useGetReleaseOrVersion() {
  const { release, version } = useSelector(getVersionAndRelease);
  return (entityType: ModelEntityType | undefined) => {
    if (entityType === undefined) {
      return undefined;
    }
    if (isProductEntityType(entityType)) {
      return version;
    } else {
      return release;
    }
  };
}

type DefaultProductDataVersionGetter = (releaseNumber: string) => string | undefined;
export function useGetDefaultVersionOfRelease(): DefaultProductDataVersionGetter {
  const { data: releases } = useGetAvailableReleases();
  if (releases === undefined) {
    return (_: string) => undefined;
  } else {
    return (releaseNumber: string) => {
      const release = releases.find(r => r.number === releaseNumber);
      return release?.defaultVersion;
    };
  }
}

/**
 * The concrete product-data model version the current page is using: the selected
 * version, or — when a release is selected — that release's default version.
 * Mirrors the resolution in `useLoadImpactSizeFactors`. Undefined until a
 * version/release is known. Every version-scoped fetch on the page keys off this
 * version, so it's the right signal for environment-availability checks.
 */
export function useSelectedProductDataVersion(): string | undefined {
  const { release, version } = useSelector(getVersionAndReleaseIfExists);
  const getDefaultVersion = useGetDefaultVersionOfRelease();
  return release ? getDefaultVersion(release) : version;
}

/**
 * The concrete product-data model version selected for the current page, taken
 * verbatim from the selection — for a company variant this is the variant's own
 * `productScoreVersion` once `useVersionReleaseManager` has applied it.
 *
 * Unlike `useSelectedProductDataVersion`, this does NOT collapse a selected
 * release to that release's default version. That matters whenever a release is
 * also selected (the normal case for a variant): the release's default version
 * lives in a different, lower-numbered series than the variant's actual version,
 * so the resolved-version hook would mask it. Use this hook for "which version
 * is the user actually looking at" checks (e.g. the Leena-2 preview marker), and
 * `useSelectedProductDataVersion` for version-scoped fetches.
 */
export function useSelectedModelVersion(): string | undefined {
  return useSelector(getVersionAndReleaseIfExists).version;
}

function isProductEntityType(entityType: ModelEntityType) {
  const types: readonly unknown[] = productLikeEntityTypes;
  return types.includes(entityType);
}

type ReleaseGatedFeature = 'value-chain-splits' | 'business-unit-splits' | 'results-of-non-material-matters';
export function useReleaseFeatureGate(feature: ReleaseGatedFeature) {
  const { release } = useSelector(getVersionAndRelease);
  if (release === null) {
    return false;
  }

  switch (feature) {
    case 'value-chain-splits':
    case 'business-unit-splits':
      return safeVersionGte(release, '1.3.0');
    case 'results-of-non-material-matters':
      return safeVersionGte(release, '1.8.0');
    default:
      return false;
  }
}
